/**
 * Cabinet presentation mode.
 *
 * The cabinet shell (marquee + bezel + control panel) is the brand. But
 * once the player is engaged with the screen (selecting a game or
 * inside a cartridge), the chrome competes for pixels — and on tablet
 * landscape aspects the chrome's vertical bulk can crowd the bezel past
 * its grid row and clip canvas content. We collapse chrome for the
 * engaged modes:
 *
 * - `attract`   — full cabinet: marquee + control panel visible. Used
 *                 at boot, the attract loop, GAME OVER, settings.
 * - `selecting` — chrome collapsed. The selector grid takes the full
 *                 viewport. Same visual mechanism `playing` uses; the
 *                 canvas's own "SELECT YOUR GAME" header carries the
 *                 brand from here on.
 * - `playing`   — chrome collapsed. Cartridge takes the full viewport.
 *
 * The CSS does all the visual work via `body[data-cabinet-mode='...']`
 * selectors in `shell.css` — this module just flips the attribute.
 */

export type CabinetMode = 'attract' | 'selecting' | 'playing';

const MODES: readonly CabinetMode[] = ['attract', 'selecting', 'playing'] as const;

/**
 * Set the cabinet's presentation mode. Idempotent — calling with the
 * same value twice is a no-op (we skip the DOM write).
 */
export function setCabinetMode(mode: CabinetMode): void {
  if (!MODES.includes(mode)) {
    throw new Error(`Unknown cabinet mode: ${mode as string}`);
  }
  if (typeof document === 'undefined') {
    // SSR / tests without DOM — nothing to do.
    return;
  }
  if (document.body.dataset.cabinetMode === mode) {
    return;
  }
  document.body.dataset.cabinetMode = mode;
}

/**
 * Read the current cabinet mode. Defaults to `attract` when nothing has
 * been set yet (matches CSS default).
 */
export function getCabinetMode(): CabinetMode {
  if (typeof document === 'undefined') {
    return 'attract';
  }
  const raw = document.body.dataset.cabinetMode;
  if (raw === 'playing' || raw === 'attract' || raw === 'selecting') {
    return raw;
  }
  return 'attract';
}
